// Requests STARTED inside fn() (synchronously: apiRequest calls fetch before its
// first await) are sent with `keepalive`, so a save fired as the page is
// closed or put away (unload, back/forward cache) still reaches the server.
// Browsers reject a keepalive request whose body is over 64 KB (a quota shared
// by every keepalive request in flight), so a larger body goes out as a plain
// fetch instead: see keepaliveFor().
let depth = 0;

export function withKeepalive<T>(fn: () => T): T {
  depth += 1;
  try { return fn(); } finally { depth -= 1; }
}

export function keepaliveActive(): boolean {
  return depth > 0;
}

// Bodies above this size are sent without keepalive (the browser's 64 KB
// quota, less headroom for other keepalive requests in flight).
export const KEEPALIVE_MAX_BODY_BYTES = 60000;

function bodySize(body: BodyInit | null | undefined): number | null {
  if (body === undefined || body === null) return 0;
  if (typeof body === 'string') return new TextEncoder().encode(body).length;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return body.size;
  if (body instanceof ArrayBuffer) return body.byteLength;
  if (ArrayBuffer.isView(body)) return body.byteLength;
  if (body instanceof URLSearchParams) return new TextEncoder().encode(body.toString()).length;
  return null; // FormData, streams: size unknown
}

// True when a request with this body should be sent with keepalive: inside
// withKeepalive() and small enough for the browser to accept it.
export function keepaliveFor(body: BodyInit | null | undefined): boolean {
  if (!keepaliveActive()) return false;
  const size = bodySize(body);
  return size !== null && size <= KEEPALIVE_MAX_BODY_BYTES;
}
